import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";

export interface PaymentInstruction {
  id: string;
  method: string;
  displayName: string;
  instructions: string | null;
  eTransferEmail: string | null;
  wireDetails: Record<string, unknown> | null;
  wechatQRCode: string | null;
  isActive: boolean;
  sortOrder: number;
}

// Account credit lives in an internal ledger (ezx-server #343), not the
// Stripe customer balance.
export interface CreditBalance {
  customerId: string;
  /** Can be negative after a deposit chargeback. */
  balanceCents: number;
  /** True while a deposit charge is under dispute — credit spends reject. */
  frozen: boolean;
}

export type CreditLedgerEntryType =
  | "DEPOSIT"
  | "MANUAL_DEPOSIT"
  | "ORDER_PAYMENT"
  | "REFUND"
  | "ADMIN_ADJUSTMENT"
  | "DEPOSIT_REVERSAL"
  | "MIGRATION";

export interface CreditLedgerEntry {
  id: string;
  /** Signed: + money in, − money out. */
  amountCents: number;
  type: CreditLedgerEntryType;
  reason: string;
  createdAt: string;
  orderId: string | null;
  balanceAfterCents: number;
}

interface CreditLedgerResponse {
  customerId: string;
  balanceCents: number;
  entries: CreditLedgerEntry[];
  nextCursor: string | null;
}

export const usePaymentInstructions = () => {
  const { token } = useSession();

  return useQuery<PaymentInstruction[], Error>({
    queryKey: ["paymentInstructions"],
    queryFn: async () => {
      if (!token) throw new Error("Not authenticated");
      const response = await axios.get<{
        success: boolean;
        data: PaymentInstruction[];
      }>(`${import.meta.env.VITE_API_URL}/api/v2/payment-instructions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.data;
    },
    enabled: !!token,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - instructions don't change often
  });
};

export const useCreditBalance = () => {
  const { customer, token } = useSession();

  return useQuery<CreditBalance, Error>({
    queryKey: ["creditBalance", customer?.id],
    queryFn: async () => {
      if (!customer?.id || !token) throw new Error("Not authenticated");
      const response = await axios.get<CreditBalance>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customer.id}/credit/balance`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    enabled: !!customer?.id && !!token,
    retry: false,
  });
};

/** Cursor-paginated credit statement — load further pages via
 * `fetchNextPage`; the first page alone may not be the full history. */
export const useCreditLedger = (take = 50) => {
  const { customer, token } = useSession();

  return useInfiniteQuery<CreditLedgerResponse, Error>({
    queryKey: ["creditLedger", customer?.id, take],
    queryFn: async ({ pageParam }) => {
      if (!customer?.id || !token) throw new Error("Not authenticated");
      const response = await axios.get<CreditLedgerResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customer.id}/credit/transactions`,
        {
          params: { take, ...(pageParam ? { cursor: pageParam } : {}) },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!customer?.id && !!token,
    retry: false,
  });
};

interface DepositCreditInput {
  amountCents: number;
  paymentMethodId: string;
  description?: string;
}

/** Self-serve deposits return an UNCONFIRMED PaymentIntent — the client
 * must confirm via stripe.js (which owns any 3DS challenge). The ledger
 * row is booked by the payment_intent.succeeded webhook afterwards. */
interface DepositCreditResponse {
  success: boolean;
  requiresConfirmation: true;
  paymentIntentId: string;
  clientSecret: string;
}

export const useCreditDeposit = () => {
  const { customer, token } = useSession();

  return useMutation<DepositCreditResponse, Error, DepositCreditInput>({
    mutationFn: async (input) => {
      if (!customer?.id || !token) throw new Error("Not authenticated");
      const response = await axios.post<DepositCreditResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customer.id}/credit/deposit`,
        input,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    // No invalidation here: the POST only creates an UNCONFIRMED intent.
    // The money moves after stripe.js confirms and the webhook books the
    // ledger row — callers refresh via useRefreshCredit at that point.
  });
};

/** Re-fetch balance + statement — call AFTER a confirmed deposit. The
 * webhook books the ledger row asynchronously, so retry on a short
 * backoff instead of trusting one immediate refetch. */
export const useRefreshCredit = () => {
  const { customer } = useSession();
  const queryClient = useQueryClient();

  return () => {
    const invalidate = () => {
      void queryClient.invalidateQueries({
        queryKey: ["creditBalance", customer?.id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["creditLedger", customer?.id],
      });
    };
    invalidate();
    setTimeout(invalidate, 2_000);
    setTimeout(invalidate, 6_000);
  };
};
